import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ModeOutlined } from '@mui/icons-material';
import {
  Button,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import {
  gray10,
  gray100,
  gray70,
  gray90,
  primaryButtonOutlinedColor,
  primaryLinkColor,
  tertiaryButtonOutlinedColor
} from '../../../constants/Colors';
import { sideBarWidth } from '../../../constants/Location';
import CustomLoading from '../../uiParts/CustomLoading';
import CustomSnackbar from '../../uiParts/CustomSnackbar';
import SideBar from '../../uiParts/SideBar';

import { AxiosError } from 'axios';
import { response } from '../../../api/db';
import { applyMaker, getCustomerInfo } from '../../../api/db/user';
import { common } from '../../../modules/error';
import { AuthContext } from '../../../contexts/auth';
import {
  userRoleEnum,
  userType as userTypeEnum
} from '../../../constants/Enum';
import CustomHead from '../CustomHead';
import { sentryLog } from '../../../config/sentry';
import EnvironmentInfoModal from '../../uiParts/EnvironmentInfoModal';
import { certificationFlgOptions } from '../../../constants/SelectOptions';

const styles = {
  changeButtonWrapper: {
    width: '70px',
    p: '0 32px 0 0',
    '@media screen and (max-width: 960px)': {
      p: '0 16px 0 0'
    }
  },
  container: {
    m: `0 0 0 ${sideBarWidth}`,
    p: `32px 32px 200px 32px`,
    '@media screen and (max-width: 960px)': {
      m: `0`,
      p: '24px 16px 120px'
    }
  },
  homepageUrl: {
    color: gray100,
    cursor: 'pointer',
    textDecorationColor: gray100,
    '&:hover': {
      opacity: '80%'
    }
  },
  nameText: {
    color: gray100,
    fontSize: '32px',
    fontWeight: '500',
    '@media screen and (max-width: 960px)': {
      fontSize: '24px'
    }
  },
  pageWrapper: {
    '@media screen and (max-width: 960px)': {
      flexDirection: 'column'
    }
  },
  primaryButtonOutlinedColor,
  primaryLinkColor,
  sanText: {
    fontSize: '28px',
    ml: '8px',
    '@media screen and (max-width: 960px)': {
      fontSize: '20px'
    }
  },
  statusBadge: {
    backgroundColor: gray90,
    borderRadius: '50%',
    height: '12px',
    mr: '8px',
    width: '12px'
  },
  table: {
    borderTop: '1px solid' + gray70,
    borderLeft: '1px solid' + gray70,
    borderRight: '1px solid' + gray70,
    m: '24px 0 48px'
  },
  tableDataColumn: {
    color: gray100,
    width: 'calc(80% - 70px)',
    '@media screen and (max-width: 960px)': {
      width: 'calc(100% - 70px)',
      '&::before': {
        color: gray90,
        content: 'attr(data-label)',
        display: 'block',
        fontSize: '14px',
        pb: '4px'
      }
    }
  },
  tableDataRow: {
    p: '16px 32px',
    '@media screen and (max-width: 960px)': {
      p: '16px'
    }
  },
  tableHeadColumn: {
    color: gray90,
    pl: '32px',
    width: '20%',
    '@media screen and (max-width: 960px)': {
      display: 'none'
    }
  },
  tableHeader: {
    backgroundColor: gray10,
    color: gray100
  },
  tableHeaderCell: {
    fontSize: '18px',
    pl: '24px',
    '@media screen and (max-width: 960px)': {
      pl: '16px'
    }
  },
  tertiaryButtonOutlinedColor
};

type User = {
  company: string;
  companyKana: string;
  companyShort: string;
  department: string;
  email: string;
  lastName: string;
  lastNameKana: string;
  firstName: string;
  firstNameKana: string;
  tel: string;
  type: number;
  homepage: string;
  industry: string;
  certificationFlg: number | null;
  certificationName: string | null;
};

const AccountInfo: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [errorText, setErrorText] = React.useState('');
  const { checkLoginStatus, loginStatus } = useContext(AuthContext);
  const isLeader = loginStatus?.role === userRoleEnum.leader;

  const [user, setUser] = React.useState<User>({
    company: '',
    companyKana: '',
    companyShort: '',
    department: '',
    email: '',
    lastName: '',
    lastNameKana: '',
    firstName: '',
    firstNameKana: '',
    tel: '',
    type: 0,
    homepage: '',
    industry: '',
    certificationFlg: null,
    certificationName: null
  });

  useEffect(() => {
    void (async () => {
      setLoading(true);
      if (!checkLoginStatus) return navigate('/auth/login');
      const result = await checkLoginStatus();
      if (!result.check) {
        navigate('/auth/login');
        return;
      }
      try {
        // ログイン中のユーザーを取得
        const response = await getCustomerInfo();
        const user = response.data;
        setUser({
          company: user.company,
          companyKana: user.company_kana,
          companyShort: user.company_short,
          department: user.department,
          email: user.email,
          lastName: user.last_name,
          lastNameKana: user.last_name_kana,
          firstName: user.first_name,
          firstNameKana: user.first_name_kana,
          tel: user.tel,
          type: user.type,
          homepage: user.homepage,
          industry: user.industry,
          certificationFlg: user.certification_flg,
          certificationName: user.certification_name
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        sentryLog(error);
        const axiosError = error as AxiosError;
        if (axiosError.isAxiosError && axiosError.response) {
          const errorResponse = axiosError.response as response;
          setErrorText(errorResponse.data.message);
          setOpen(true);
        } else {
          setErrorText(common);
          setOpen(true);
        }
        throw new Error('get user error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSubmit = async () => {
    setLoading(true);
    try {
      // 建材登録会員の申請
      await applyMaker();
      navigate('/exhibit_apply/complete');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      sentryLog(error);
      const axiosError = error as AxiosError;
      if (axiosError.isAxiosError && axiosError.response) {
        const errorResponse = axiosError.response as response;
        setErrorText(errorResponse.data.message);
        setOpen(true);
      } else {
        setErrorText(common);
        setOpen(true);
      }
      throw new Error('apply maker error');
    } finally {
      setLoading(false);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleEdit = () => {
    setModalOpen(false);
    navigate('/account/basic');
  };

  const handleClick = async () => {
    // 会員情報からcertification_flgを確認
    const res = await getCustomerInfo();
    const certificationFlg = res.data.certification_flg;
    if (certificationFlg === null) {
      // nullの場合は入力を促すモーダルを表示
      handleOpenModal();
    } else {
      onSubmit();
    }
  };

  // 会員種別テーブルのデータ
  const userTypeTableData = (props: { type: number }) => {
    let content;
    switch (props.type) {
      case userTypeEnum.view:
      case userTypeEnum.rejectedMaker:
        content = (
          <>
            <TableRow>
              <TableCell sx={styles.tableDataRow}>閲覧ユーザー</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={styles.tableDataRow}>
                自社の建材を登録する場合は、登録会員への変更手続きが必要です。
                <br />
                <Button
                  onClick={handleClick}
                  sx={[styles.primaryButtonOutlinedColor, { m: '16px 0 8px' }]}
                  variant="outlined"
                >
                  建材登録会員の申し込み
                </Button>
                <EnvironmentInfoModal
                  open={modalOpen}
                  onClose={handleCloseModal}
                  onEdit={handleEdit}
                />
              </TableCell>
            </TableRow>
          </>
        );
        break;
      case userTypeEnum.maker:
        content = (
          <TableRow>
            <TableCell sx={styles.tableDataRow}>建材登録ユーザー</TableCell>
          </TableRow>
        );
        break;
      case userTypeEnum.applyingMaker:
        content = (
          <>
            <TableRow>
              <TableCell sx={styles.tableDataRow}>閲覧ユーザー</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={styles.tableDataRow}>
                建材登録会員の申請を受け付けました。完了次第メールでお知らせします。
                <br />
                <Button
                  disabled
                  sx={[styles.primaryButtonOutlinedColor, { m: '16px 0 8px' }]}
                  variant="outlined"
                >
                  建材登録会員の申し込み済み
                </Button>
              </TableCell>
            </TableRow>
          </>
        );
        break;
      case userTypeEnum.admin:
        content = (
          <TableRow>
            <TableCell sx={styles.tableDataRow}>事務局ユーザー</TableCell>
          </TableRow>
        );
        break;
    }
    return content;
  };

  // 基本情報テーブルのデータ
  const basicInfoTableList = [
    {
      label: '会社名',
      data: user.company
    },
    {
      label: '会社名 (フリガナ)',
      data: user.companyKana
    },
    {
      label: '会社名略称',
      data: user.companyShort
    },
    {
      label: '部署名',
      data: user.department
    },
    {
      label: '担当者',
      data: user.lastName + ' ' + user.firstName
    },
    {
      label: '担当者 (フリガナ)',
      data: user.lastNameKana + ' ' + user.firstNameKana
    },
    ...(isLeader
      ? [
          {
            label: '電話番号',
            data: user.tel
          }
        ]
      : []),
    {
      label: '企業ホームページURL',
      data: (
        <Link href={user.homepage} sx={styles.homepageUrl} target="_blank">
          {user.homepage}
        </Link>
      )
    },
    {
      label: '業種',
      data: user.industry
    },
    {
      label: 'SBT等の企業の環境認定',
      data:
        user.certificationFlg === certificationFlgOptions[1].value
          ? '取得済'
          : user.certificationFlg === certificationFlgOptions[0].value
          ? '未取得'
          : ''
    },
    {
      label: '認定の名称',
      data: user.certificationName
    }
  ];

  return (
    <>
      <CustomHead title="アカウント管理 | CMCOS[コムコス]" />
      <CustomLoading loading={loading} />
      <Grid container sx={styles.pageWrapper}>
        <SideBar />

        <Grid container direction="column" item sx={styles.container}>
          <Typography sx={styles.nameText}>
            {user.lastName} {user.firstName}
            <Typography component="span" sx={styles.sanText}>
              さん
            </Typography>
          </Typography>

          <TableContainer>
            <Table sx={styles.table}>
              <TableHead>
                <TableRow sx={styles.tableHeader}>
                  <TableCell sx={styles.tableHeaderCell}>会員種別</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{userTypeTableData({ type: user.type })}</TableBody>
            </Table>

            <Table sx={styles.table}>
              <TableHead>
                <TableRow sx={styles.tableHeader}>
                  <TableCell colSpan={3} sx={styles.tableHeaderCell}>
                    ログイン情報
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ borderRight: '1px solid' + gray70 }}>
                <TableRow>
                  <TableCell sx={styles.tableHeadColumn}>
                    メールアドレス
                  </TableCell>
                  <TableCell
                    data-label="メールアドレス"
                    sx={styles.tableDataColumn}
                  >
                    {user.email}
                  </TableCell>
                  <TableCell sx={styles.changeButtonWrapper}>
                    <Button
                      size="small"
                      startIcon={<ModeOutlined sx={{ color: gray90 }} />}
                      sx={styles.tertiaryButtonOutlinedColor}
                      variant="outlined"
                      href="/account/email"
                    >
                      変更
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={styles.tableHeadColumn}>パスワード</TableCell>
                  <TableCell
                    data-label="パスワード"
                    sx={styles.tableDataColumn}
                  >
                    ●●●●●●
                  </TableCell>
                  <TableCell sx={styles.changeButtonWrapper}>
                    <Button
                      size="small"
                      startIcon={<ModeOutlined sx={{ color: gray90 }} />}
                      sx={styles.tertiaryButtonOutlinedColor}
                      variant="outlined"
                      href="/auth/password_reset"
                    >
                      変更
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Table sx={styles.table}>
              <TableHead>
                <TableRow sx={styles.tableHeader}>
                  <TableCell colSpan={2} sx={styles.tableHeaderCell}>
                    基本情報
                  </TableCell>
                  <TableCell sx={styles.changeButtonWrapper}>
                    <Button
                      size="small"
                      startIcon={<ModeOutlined sx={{ color: gray90 }} />}
                      sx={styles.tertiaryButtonOutlinedColor}
                      variant="outlined"
                      href="/account/basic"
                    >
                      変更
                    </Button>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {basicInfoTableList.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell sx={styles.tableHeadColumn}>
                      {item.label}
                    </TableCell>
                    <TableCell
                      colSpan={3}
                      data-label={item.label}
                      sx={styles.tableDataColumn}
                    >
                      {item.data}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Link sx={styles.primaryLinkColor} href="/account/withdraw">
            退会申請をする
          </Link>
        </Grid>
      </Grid>
      <CustomSnackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        text={errorText}
      />
    </>
  );
};

export default AccountInfo;
